import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, useAuthStore } from '@/store';
import {
  Bot, KeyRound, Sparkles, Store, Clock, Zap, SlidersHorizontal, Mic,
  CheckCircle2, AlertTriangle, ChevronRight, Loader2, ExternalLink, X, BookOpen,
  Plus, Trash2, Smartphone,
} from 'lucide-react';

const DAYS = [
  { v: 0, l: 'Dom' }, { v: 1, l: 'Seg' }, { v: 2, l: 'Ter' }, { v: 3, l: 'Qua' },
  { v: 4, l: 'Qui' }, { v: 5, l: 'Sex' }, { v: 6, l: 'Sáb' },
];
const DIAS_SEMANA = [
  { v: '0', lFull: 'Dom' }, { v: '1', lFull: 'Seg' }, { v: '2', lFull: 'Ter' }, { v: '3', lFull: 'Qua' },
  { v: '4', lFull: 'Qui' }, { v: '5', lFull: 'Sex' }, { v: '6', lFull: 'Sáb' },
];
const TONES = [
  { v: 'formal', l: 'Formal e profissional' },
  { v: 'amigavel', l: 'Amigável e acolhedor' },
  { v: 'descontraido', l: 'Leve e descontraído' },
  { v: 'carinhoso', l: 'Carinhoso e afetivo' },
];
const EMOJIS = [
  { v: 'none', l: 'Nenhum' }, { v: 'low', l: 'Poucos' },
  { v: 'medium', l: 'Moderado' }, { v: 'high', l: 'Bastante' },
];
const LENGTHS = [
  { v: 'short', l: 'Curtas (ideal WhatsApp)' },
  { v: 'medium', l: 'Médias' }, { v: 'long', l: 'Detalhadas' },
];
const ROLES = [
  { v: 'sales', l: 'Vendedor (foco em vender)' },
  { v: 'support', l: 'Suporte (foco em resolver)' },
  { v: 'both', l: 'Vendas + Suporte' },
];

const DEFAULT = {
  name: 'Meu Assistente',
  enabled: false,
  system_prompt: '',
  trigger_mode: 'open',
  start_keywords: [],
  stop_keywords: ['parar', 'humano', 'atendente'],
  welcome_message: '',
  transfer_message: 'Certo! Vou transferir você para um atendente humano. Aguarde um momento.',
  auto_return_minutes: 0,
  transcrever_audio: false,
  audio_ignored_numbers: [],
  teste_enabled: false, teste_link: '', teste_keywords: ['teste','testar','quero teste'], teste_limite_por_numero: 1, teste_msg_limite: 'Você já solicitou o teste disponível. Para assinar, fale com o suporte. 😊',
  responder_por_audio: false,
  response_delay_min: 2,
  response_delay_max: 3,
  connection_id: '',
  business_hours: {
    enabled: false, start_time: '08:00', end_time: '18:00', days: [1, 2, 3, 4, 5],
    outside_message: 'Olá! No momento estou fora do horário de atendimento. Retornarei em breve!',
    horarios_por_dia: null,
    feriados: [],
  },
  ignored_numbers: [],
  tone: 'amigavel', emoji_level: 'low', response_length: 'short', agent_role: 'both',
  business_name: '', business_info: { products_list: [], payment: '', hours: '', rules: '', extra: '' },
  can_send_payment_link: false,
};

export default function AIAssistantPage() {
  const { user } = useAuthStore();
  const isSimple = user?.role === 'simple';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [a, setA] = useState(DEFAULT);
  const [modal, setModal] = useState(null);
  const [geminiKey, setGeminiKey] = useState('');
  const [hasGeminiKey, setHasGeminiKey] = useState(false);
  const [geminiKeys, setGeminiKeys] = useState([]);
  const [savingKey, setSavingKey] = useState(false);
  const [conexoes, setConexoes] = useState([]);
  const [connSel, setConnSel] = useState('_geral');  // '_geral' = IA geral, ou id da conexão

  const set = (patch) => setA((p) => ({ ...p, ...patch }));
  const setBiz = (patch) => setA((p) => ({ ...p, business_info: { ...p.business_info, ...patch } }));
  const setHours = (patch) => setA((p) => ({ ...p, business_hours: { ...p.business_hours, ...patch } }));
  const products = a.business_info?.products_list || [];
  const addProduct = () => setBiz({ products_list: [...products, { name: '', description: '', prices: '' }] });
  const updProduct = (i, patch) => setBiz({ products_list: products.map((p, idx) => idx === i ? { ...p, ...patch } : p) });
  const delProduct = (i) => setBiz({ products_list: products.filter((_, idx) => idx !== i) });

  const carregarIA = async (connId) => {
    try {
      const params = (connId && connId !== '_geral') ? { connection_id: connId } : {};
      const r = await api.get('/ai-assistant', { params });
      if (r.data) setA((p) => ({
        ...DEFAULT, ...r.data,
        connection_id: (connId && connId !== '_geral') ? connId : '',
        business_info: { ...DEFAULT.business_info, ...(r.data.business_info || {}) },
        business_hours: { ...DEFAULT.business_hours, ...(r.data.business_hours || {}) },
      }));
    } catch { setA((p) => ({ ...DEFAULT, connection_id: (connId && connId !== '_geral') ? connId : '' })); }
  };

  useEffect(() => {
    (async () => {
      await carregarIA('_geral');
      try { const k = await api.get('/ai-assistant/gemini-key'); setHasGeminiKey(!!k.data.has_key); } catch {}
      try { const ks = await api.get('/ai-assistant/gemini-keys'); setGeminiKeys(ks.data.keys || []); setHasGeminiKey((ks.data.total || 0) > 0); } catch {}
      try { const cx = await api.get('/connections'); setConexoes(Array.isArray(cx.data) ? cx.data : (cx.data.connections || [])); } catch {}
      setLoading(false);
    })();
  }, []);

  // trocar de conexão recarrega a IA daquela conexão
  const trocarConexao = async (connId) => {
    setConnSel(connId);
    setLoading(true);
    await carregarIA(connId);
    setLoading(false);
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const payload = { ...a, ...(patch || {}), connection_id: (connSel && connSel !== '_geral') ? connSel : '' };
      // só envia 'enabled' se ele estiver EXPLICITAMENTE no patch (evita desligar a IA sem querer)
      if (!(patch && 'enabled' in patch)) delete payload.enabled;
      await api.post('/ai-assistant', payload);
      if (patch) setA((p) => ({ ...p, ...patch }));
      toast.success('Salvo!');
      setModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const recarregarChaves = async () => {
    try { const ks = await api.get('/ai-assistant/gemini-keys'); setGeminiKeys(ks.data.keys || []); setHasGeminiKey((ks.data.total || 0) > 0); } catch {}
  };
  const saveGeminiKey = async () => {
    if (!geminiKey.trim()) { toast.error('Cole sua chave Gemini'); return; }
    setSavingKey(true);
    try {
      await api.post('/ai-assistant/gemini-keys', { gemini_api_key: geminiKey.trim() });
      toast.success('Chave adicionada!'); setGeminiKey(''); await recarregarChaves();
    } catch (e) { toast.error(e.response?.data?.detail || 'Chave inválida'); }
    finally { setSavingKey(false); }
  };
  const removeGeminiKeyAt = async (index) => {
    setSavingKey(true);
    try { await api.delete(`/ai-assistant/gemini-keys/${index}`); toast.success('Chave removida'); await recarregarChaves(); }
    catch { toast.error('Erro'); } finally { setSavingKey(false); }
  };

  const toggleEnabled = async (v) => { set({ enabled: v }); await save({ enabled: v }); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const steps = [
    { done: hasGeminiKey, label: 'Chave Gemini' },
    { done: products.length > 0, label: 'Dados do negócio' },
    { done: !!(a.system_prompt || products.length > 0), label: 'Personalidade' },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const ready = hasGeminiKey;
  const bizFilled = (products.filter(p => (p.name||'').trim() || (p.prices||'').trim()).length) + ['payment', 'hours', 'rules', 'extra'].filter((k) => (a.business_info?.[k] || '').trim()).length;

  const tile = (id, icon, title, desc, status, semIA) => (
    <button onClick={() => setModal(id)}
      className="text-left rounded-2xl border border-border/60 bg-card hover:border-primary/40 hover:shadow-sm transition-all p-4 flex items-start gap-3 group">
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold">{title}</p>{status}{semIA && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">⚡ Sem IA</span>}</div>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{desc}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary mt-1 shrink-0" />
    </button>
  );

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      {/* Cabeçalho */}
      <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-primary/[0.07] to-transparent p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center"><Bot className="w-6 h-6" /></div>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                IA de Atendimento
                {a.enabled ? <Badge className="bg-green-500/15 text-green-400 border-green-500/30">Ativa</Badge>
                  : <Badge variant="outline" className="text-muted-foreground">Desativada</Badge>}
              </h1>
              <p className="text-sm text-muted-foreground">Seu atendente virtual no WhatsApp, com a cara do seu negócio.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setModal('tutorial')}><BookOpen className="w-4 h-4 mr-1.5" /> Como configurar</Button>
            <div className="flex items-center gap-2 rounded-xl border border-border/60 px-3 py-2">
              <span className="text-sm font-medium">{a.enabled ? 'Ligada' : 'Desligada'}</span>
              <Switch checked={a.enabled} onCheckedChange={toggleEnabled} disabled={!ready} />
            </div>
          </div>
        </div>

        {!ready && (
          <div className="mt-4 flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /> Configure sua <b>chave Gemini</b> para ligar o assistente.
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {steps.map((s, i) => (
            <span key={i} className={`text-xs flex items-center gap-1.5 rounded-full px-2.5 py-1 border ${s.done ? 'text-green-400 border-green-500/25 bg-green-500/5' : 'text-muted-foreground border-border/50'}`}>
              {s.done ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-block" />}
              {s.label}
            </span>
          ))}
          <span className="text-xs text-muted-foreground ml-auto">{doneCount}/{steps.length} configurado</span>
        </div>
      </div>

      {/* Seletor de conexão: cada conexão tem sua própria IA */}
      {conexoes.length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card p-3">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Configurando a IA de:</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => trocarConexao('_geral')}
              className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${connSel === '_geral' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted/50'}`}>
              🌐 Geral (todas)
            </button>
            {conexoes.map((c) => (
              <button key={c.id} onClick={() => trocarConexao(c.id)}
                className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors flex items-center gap-1.5 ${connSel === c.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted/50'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${(c.status === 'connected' || c.status?.connected) ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                {c.name}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            {connSel === '_geral'
              ? 'A IA "Geral" responde nas conexões que não têm IA própria configurada.'
              : 'Esta IA responde apenas nesta conexão, de forma independente.'}
          </p>
        </div>
      )}

      {isSimple && (
        <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.05] p-4 text-sm">
          <p className="font-medium mb-1">🔑 Configure sua chave Gemini para usar o NEXU IA</p>
          <p className="text-muted-foreground text-xs">Seu plano usa a chave Gemini para o assistente NEXU (por voz e texto). As demais funções de atendimento no WhatsApp não estão no seu plano.</p>
        </div>
      )}

      {/* Grade de cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tile('gemini', <KeyRound className="w-5 h-5" />, 'Chave Gemini',
          hasGeminiKey ? 'Sua chave está configurada e segura.' : 'Obrigatória. Grátis no Google AI Studio.',
          hasGeminiKey ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-[10px]">Pendente</Badge>)}
        {!isSimple && tile('business', <Store className="w-5 h-5" />, 'Dados do negócio',
          products.length > 0 ? `${products.length} produto(s) cadastrado(s)` : 'Cadastre seus produtos, preços, pagamento…',
          bizFilled > 0 ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : null)}
        {!isSimple && tile('personality', <Sparkles className="w-5 h-5" />, 'Personalidade',
          `${ROLES.find(r => r.v === a.agent_role)?.l.split(' ')[0]} · ${TONES.find(t => t.v === a.tone)?.l.split(' ')[0]}`, null)}
        {!isSimple && tile('triggers', <Zap className="w-5 h-5" />, 'Gatilhos e transferência',
          a.trigger_mode === 'keyword' ? 'Responde por palavra-chave.' : 'Responde a qualquer mensagem.', null)}
        {!isSimple && tile('hours', <Clock className="w-5 h-5" />, 'Horário de atendimento',
          a.business_hours?.enabled ? 'Horário por dia + feriados configurados' : 'Sempre ativo (24h).', null, a.business_hours?.independente)}
        {!isSimple && tile('advanced', <SlidersHorizontal className="w-5 h-5" />, 'Avançado',
          `Delay ${a.response_delay_min}-${a.response_delay_max}s · ${a.ignored_numbers.length} nº ignorado(s)`, null)}
        {!isSimple && tile('audio', <Mic className="w-5 h-5" />, 'Áudio',
          `${a.transcrever_audio ? 'Transcrição ligada' : 'Transcrição desligada'}${a.responder_por_audio ? ' · aceita resposta por áudio' : ''}`, null, a.transcrever_audio)}
        {!isSimple && tile('teste', <Zap className="w-5 h-5" />, 'Teste IPTV (Sigma)',
          a.teste_enabled ? 'Teste automático ligado' : 'Gera teste automático quando o cliente pede', null, a.teste_enabled)}
      </div>

      {/* Chave Gemini */}
      <Dialog open={modal === 'gemini'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary" /> Sua chave Gemini</DialogTitle>
            <DialogDescription>Obrigatória e gratuita. É ela que dá "cérebro" à IA. As respostas usam a sua cota do Google, não a nossa. A chave é guardada criptografada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <a href="https://aistudio.google.com/api-keys" target="_blank" rel="noreferrer"
               className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm hover:bg-primary/10">
              <span className="flex items-center gap-2 text-primary font-medium"><ExternalLink className="w-4 h-4" /> Gerar chave grátis no Google AI Studio</span>
              <ChevronRight className="w-4 h-4 text-primary" />
            </a>
            {/* Lista de chaves cadastradas */}
            {geminiKeys.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs">Chaves cadastradas ({geminiKeys.length}) — usadas em rotação</Label>
                {geminiKeys.map((k) => (
                  <div key={k.index} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
                    <span className="text-xs font-mono flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> {k.mask}</span>
                    <button onClick={() => removeGeminiKeyAt(k.index)} disabled={savingKey} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">💡 Com várias chaves, se uma esgotar a cota do dia, a IA usa a próxima automaticamente.</p>
              </div>
            )}
            <div>
              <Label className="text-xs">{geminiKeys.length > 0 ? 'Adicionar outra chave' : 'Sua chave Gemini'}</Label>
              <p className="text-[11px] text-muted-foreground mb-1">Entre no Google → "Criar chave de API" → copie (começa com <code className="bg-muted px-1 rounded">AIza…</code>).</p>
              <div className="flex gap-2">
                <Input type="password" value={geminiKey} onChange={(e) => setGeminiKey(e.target.value)} placeholder="Cole a chave aqui" onKeyDown={(e) => e.key === 'Enter' && saveGeminiKey()} />
                <Button onClick={saveGeminiKey} disabled={savingKey || !geminiKey.trim()} className="shrink-0">{savingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dados do negócio */}
      <Dialog open={modal === 'business'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Store className="w-5 h-5 text-primary" /> Dados do negócio</DialogTitle>
            <DialogDescription>Preencha o que souber. A IA usa tudo isso para atender como você atenderia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Nome da sua revenda / marca</Label><Input value={a.business_name} onChange={(e) => set({ business_name: e.target.value })} placeholder="Ex.: Village TV" /></div>

            {/* Lista de produtos (pode ter vários) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Seus produtos / serviços</Label>
                <span className="text-[11px] text-muted-foreground">Adicione quantos vender</span>
              </div>
              <div className="space-y-3">
                {products.length === 0 && (
                  <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/60 p-3 text-center">
                    Nenhum produto ainda. Clique em "Adicionar produto" abaixo.
                  </p>
                )}
                {products.map((prod, i) => (
                  <div key={i} className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-2 relative">
                    <button onClick={() => delProduct(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-red-500" title="Remover">
                      <X className="w-4 h-4" />
                    </button>
                    <div className="text-[11px] font-semibold text-primary">Produto {i + 1}</div>
                    <Input value={prod.name} onChange={(e) => updProduct(i, { name: e.target.value })} placeholder="Nome (ex.: IPTV Completo, App de Filmes, VPN...)" />
                    <Textarea rows={2} value={prod.description} onChange={(e) => updProduct(i, { description: e.target.value })} placeholder="Descrição (ex.: +20 mil canais, filmes e séries em 4K, funciona em qualquer aparelho)" />
                    <Textarea rows={2} value={prod.prices} onChange={(e) => updProduct(i, { prices: e.target.value })} placeholder="Preços/planos deste produto (ex.: Mensal R$30 · Trimestral R$75 · Anual R$300)" />
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-2 border-dashed" onClick={addProduct}>
                + Adicionar produto
              </Button>
            </div>

            {/* Outros dados */}
            {[
              { k: 'payment', l: 'Como o cliente paga', ph: 'Ex.: PIX ou link. Acesso liberado na hora após o pagamento.' },
              { k: 'hours', l: 'Horário / disponibilidade', ph: 'Ex.: Suporte humano de seg a sáb, 9h às 22h.' },
              { k: 'rules', l: 'Regras importantes', ph: 'Ex.: Teste grátis de 3h. Não vendemos sem pagamento antecipado.' },
              { k: 'extra', l: 'Outras informações', ph: 'Promoções, canais específicos, etc.' },
            ].map(({ k, l, ph }) => (
              <div key={k}><Label className="text-xs">{l}</Label><Textarea rows={2} value={a.business_info?.[k] || ''} onChange={(e) => setBiz({ [k]: e.target.value })} placeholder={ph} /></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Personalidade */}
      <Dialog open={modal === 'personality'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Personalidade da IA</DialogTitle>
            <DialogDescription>Defina o jeitão do seu atendente. Combina com os dados do negócio automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nome do assistente</Label><Input value={a.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ex.: Sofia, Assistente Village" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Função</Label>
                <Select value={a.agent_role} onValueChange={(v) => set({ agent_role: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Tom de voz</Label>
                <Select value={a.tone} onValueChange={(v) => set({ tone: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Emojis</Label>
                <Select value={a.emoji_level} onValueChange={(v) => set({ emoji_level: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EMOJIS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Tamanho das respostas</Label>
                <Select value={a.response_length} onValueChange={(v) => set({ response_length: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LENGTHS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Instruções extras (opcional)</Label>
              <Textarea rows={5} value={a.system_prompt} onChange={(e) => set({ system_prompt: e.target.value })}
                placeholder="Regras específicas, o que nunca fazer, exemplos de resposta… A IA já sabe os dados do negócio; aqui é o toque final." />
              <p className="text-[11px] text-muted-foreground mt-1">Dica: escreva <code className="bg-muted px-1 rounded">[TRANSFERIR]</code> nas regras para a IA passar para um humano quando o cliente pedir.</p>
            </div>
            <div><Label className="text-xs">Mensagem de boas-vindas (opcional)</Label>
              <Textarea rows={2} value={a.welcome_message} onChange={(e) => set({ welcome_message: e.target.value })}
                placeholder="Ex.: Olá! 👋 Sou a Sofia, da Village TV. Como posso ajudar?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gatilhos */}
      <Dialog open={modal === 'triggers'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Gatilhos e transferência</DialogTitle>
            <DialogDescription>Quando a IA deve responder e quando passar para um humano.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Quando a IA responde?</Label>
              <Select value={a.trigger_mode} onValueChange={(v) => set({ trigger_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Qualquer mensagem recebida</SelectItem>
                  <SelectItem value="keyword">Só quando enviarem uma palavra-chave</SelectItem>
                </SelectContent></Select></div>
            {a.trigger_mode === 'keyword' && (
              <KeywordEditor label="Palavras que iniciam o atendimento" value={a.start_keywords} onChange={(v) => set({ start_keywords: v })} placeholder="ex.: oi, quero, iptv" />
            )}
            <KeywordEditor label="Palavras que transferem para humano" value={a.stop_keywords} onChange={(v) => set({ stop_keywords: v })} placeholder="ex.: humano, atendente" />
            <div><Label className="text-xs">Mensagem ao transferir</Label><Textarea rows={2} value={a.transfer_message} onChange={(e) => set({ transfer_message: e.target.value })} /></div>
            <div>
              <Label className="text-xs">Voltar a responder após (minutos)</Label>
              <Input type="number" min="0" value={a.auto_return_minutes || 0} onChange={(e) => set({ auto_return_minutes: parseInt(e.target.value) || 0 })} />
              <p className="text-[11px] text-muted-foreground mt-1">Quando a IA transfere para humano ou é parada, ela volta a responder esse contato depois deste tempo. 0 = nunca volta sozinha.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Horário */}
      <Dialog open={modal === 'hours'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> Horário de atendimento</DialogTitle>
            <DialogDescription>Defina os horários de cada dia e os feriados. Fora do horário, a IA envia a mensagem configurada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
              <span className="text-sm">Limitar por horário</span>
              <Switch checked={a.business_hours.enabled} onCheckedChange={(v) => setHours({ enabled: v })} />
            </div>
            {a.business_hours.enabled && (
              <>
                <label className="flex items-center justify-between rounded-lg border border-cyan-500/30 bg-cyan-500/[0.05] px-3 py-2.5">
                  <div>
                    <span className="text-sm flex items-center gap-1.5">⚡ Funciona sem a IA ligada</span>
                    <p className="text-[11px] text-muted-foreground">Responde a mensagem de fora do horário mesmo com a IA desativada.</p>
                  </div>
                  <Switch checked={!!a.business_hours.independente} onCheckedChange={(v) => setHours({ independente: v })} />
                </label>
                <div>
                  <Label className="text-xs">Reenviar aviso a cada (horas)</Label>
                  <Input type="number" min="0" value={a.business_hours.fora_horario_cooldown ?? 3} onChange={(e) => setHours({ fora_horario_cooldown: parseInt(e.target.value) || 0 })} />
                  <p className="text-[11px] text-muted-foreground mt-1">Evita mandar o aviso toda hora. Ex: 3 = o cliente só recebe o aviso 1x a cada 3h. 0 = sempre avisa.</p>
                </div>
                {/* Horário por dia da semana */}
                <div>
                  <Label className="text-xs mb-1.5 block">Horário de cada dia</Label>
                  <div className="space-y-1.5">
                    {DIAS_SEMANA.map((d) => {
                      const hpd = a.business_hours.horarios_por_dia || {};
                      const cfg = hpd[d.v] || { aberto: (a.business_hours.days || [1,2,3,4,5]).includes(d.v), inicio: a.business_hours.start_time || '08:00', fim: a.business_hours.end_time || '18:00' };
                      const setDia = (patch) => setHours({ horarios_por_dia: { ...hpd, [d.v]: { ...cfg, ...patch } } });
                      return (
                        <div key={d.v} className="flex items-center gap-2 rounded-lg border border-border/50 px-2.5 py-1.5">
                          <button type="button" onClick={() => setDia({ aberto: !cfg.aberto })}
                            className={`w-9 h-6 rounded-full transition-colors relative shrink-0 ${cfg.aberto ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}>
                            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.aberto ? 'left-[18px]' : 'left-0.5'}`} />
                          </button>
                          <span className="text-sm font-medium w-10 shrink-0">{d.lFull}</span>
                          {cfg.aberto ? (
                            <div className="flex items-center gap-1.5 flex-1 justify-end">
                              <Input type="time" value={cfg.inicio} onChange={(e) => setDia({ inicio: e.target.value })} className="h-8 w-[100px] text-xs" />
                              <span className="text-muted-foreground text-xs">até</span>
                              <Input type="time" value={cfg.fim} onChange={(e) => setDia({ fim: e.target.value })} className="h-8 w-[100px] text-xs" />
                            </div>
                          ) : <span className="text-xs text-muted-foreground flex-1 text-right">Fechado</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Feriados */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs">Feriados / datas especiais</Label>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setHours({ feriados: [...(a.business_hours.feriados || []), { data: '', nome: '', fechado: true, inicio: '08:00', fim: '18:00' }] })}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                    </Button>
                  </div>
                  {(a.business_hours.feriados || []).length === 0 && <p className="text-[11px] text-muted-foreground">Nenhum feriado cadastrado.</p>}
                  <div className="space-y-2">
                    {(a.business_hours.feriados || []).map((f, i) => {
                      const setFer = (patch) => setHours({ feriados: (a.business_hours.feriados || []).map((x, idx) => idx === i ? { ...x, ...patch } : x) });
                      const rmFer = () => setHours({ feriados: (a.business_hours.feriados || []).filter((_, idx) => idx !== i) });
                      return (
                        <div key={i} className="rounded-lg border border-border/50 p-2.5 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input type="date" value={f.data} onChange={(e) => setFer({ data: e.target.value })} className="h-8 text-xs flex-1" />
                            <Input value={f.nome} onChange={(e) => setFer({ nome: e.target.value })} placeholder="Nome (ex: Natal)" className="h-8 text-xs flex-1" />
                            <button type="button" onClick={rmFer} className="text-rose-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setFer({ fechado: !f.fechado })}
                              className={`text-xs px-2.5 py-1 rounded-lg border ${f.fechado ? 'bg-rose-500/15 border-rose-500/30 text-rose-500' : 'border-border text-muted-foreground'}`}>
                              {f.fechado ? '🔒 Fechado o dia todo' : 'Horário especial'}
                            </button>
                            {!f.fechado && (
                              <div className="flex items-center gap-1.5 flex-1 justify-end">
                                <Input type="time" value={f.inicio} onChange={(e) => setFer({ inicio: e.target.value })} className="h-8 w-[90px] text-xs" />
                                <span className="text-muted-foreground text-xs">até</span>
                                <Input type="time" value={f.fim} onChange={(e) => setFer({ fim: e.target.value })} className="h-8 w-[90px] text-xs" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mensagem fora do horário - botão que abre sub-card */}
                <button type="button" onClick={() => setModal('fora_horario')}
                  className="w-full flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5 hover:bg-muted/40 transition-colors">
                  <div className="text-left">
                    <p className="text-sm font-medium">💬 Mensagem de fora do horário</p>
                    <p className="text-[11px] text-muted-foreground truncate max-w-[280px]">{a.business_hours.outside_message || 'Toque para personalizar'}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mensagem de fora do horário (card dedicado) */}
      <Dialog open={modal === 'fora_horario'} onOpenChange={(o) => !o && setModal('hours')}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">💬 Mensagem de fora do horário</DialogTitle>
            <DialogDescription>Enviada automaticamente quando alguém chama fora do horário de atendimento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea rows={5} value={a.business_hours.outside_message} onChange={(e) => setHours({ outside_message: e.target.value })}
              placeholder="Olá! No momento estamos fora do horário de atendimento..." />
            <div className="rounded-lg bg-muted/40 p-2.5">
              <p className="text-[11px] text-muted-foreground mb-1">Variáveis disponíveis:</p>
              <div className="flex flex-wrap gap-1">
                {['{nome}', '{telefone}', '{horario_hoje}', '{proximo_horario}'].map((v) => (
                  <button key={v} type="button" onClick={() => setHours({ outside_message: (a.business_hours.outside_message || '') + v })}
                    className="text-[10px] font-mono bg-background px-1.5 py-0.5 rounded hover:text-primary">{v}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal('hours')}>Voltar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avançado */}
      <Dialog open={modal === 'conexao'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-primary" /> Conexão da IA</DialogTitle>
            <DialogDescription>Escolha em qual conexão a IA vai responder.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {conexoes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhuma conexão WhatsApp encontrada. Conecte um número primeiro.</p>
            ) : (
              <>
                <button onClick={() => set({ connection_id: '' })}
                  className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${!a.connection_id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                  <div>
                    <p className="text-sm font-medium">Todas as conexões</p>
                    <p className="text-[11px] text-muted-foreground">A IA responde em qualquer número conectado</p>
                  </div>
                  {!a.connection_id && <CheckCircle2 className="w-5 h-5 text-primary" />}
                </button>
                {conexoes.map((c) => (
                  <button key={c.id} onClick={() => set({ connection_id: c.id })}
                    className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${a.connection_id === c.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${(c.status === 'connected' || c.status?.connected) ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        {c.phone_number && <p className="text-[11px] text-muted-foreground">{c.phone_number}</p>}
                      </div>
                    </div>
                    {a.connection_id === c.id && <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />}
                  </button>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === 'advanced'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-primary" /> Avançado</DialogTitle>
            <DialogDescription>Ajustes finos de comportamento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Delay mín. (s)</Label><Input type="number" min="0" value={a.response_delay_min} onChange={(e) => set({ response_delay_min: parseInt(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs">Delay máx. (s)</Label><Input type="number" min="0" value={a.response_delay_max} onChange={(e) => set({ response_delay_max: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">Um pequeno atraso deixa a resposta mais natural (parece que está digitando).</p>
            <KeywordEditor label="Números ignorados (a IA nunca responde)" value={a.ignored_numbers} onChange={(v) => set({ ignored_numbers: v })} placeholder="ex.: 5521999999999" numeric />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Áudio */}
      <Dialog open={modal === 'audio'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mic className="w-5 h-5 text-primary" /> Áudio</DialogTitle>
            <DialogDescription>Transcrição de áudios recebidos e como a IA lida com respostas por áudio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="flex items-center justify-between rounded-lg bg-muted/40 p-3 cursor-pointer">
              <div>
                <span className="text-sm font-medium flex items-center gap-2">🎙️ Transcrever áudios recebidos</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">Todo áudio que o cliente enviar é transcrito e devolvido em texto. Funciona mesmo com a IA de atendimento DESLIGADA — ótimo se você só quer transcrever.</p>
              </div>
              <Switch checked={!!a.transcrever_audio} onCheckedChange={(v) => set({ transcrever_audio: v })} />
            </label>
            <label className="flex items-center justify-between rounded-lg bg-muted/40 p-3 cursor-pointer">
              <div>
                <span className="text-sm font-medium flex items-center gap-2">🎧 Aceitar resposta por áudio</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">Quando a IA faz uma pergunta e o cliente responde por áudio, ela transcreve, entende e prossegue o atendimento. Requer a IA de atendimento LIGADA.</p>
              </div>
              <Switch checked={!!a.responder_por_audio} onCheckedChange={(v) => set({ responder_por_audio: v })} />
            </label>
            <KeywordEditor label="Números que NÃO transcrevem" value={a.audio_ignored_numbers} onChange={(v) => set({ audio_ignored_numbers: v })} placeholder="ex.: 5521999999999" numeric />
            <p className="text-[11px] text-muted-foreground">Áudios desses números nunca são transcritos. A transcrição usa a chave Gemini configurada (ou a do sistema).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teste IPTV (Sigma) */}
      <Dialog open={modal === 'teste'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Teste IPTV automático (Sigma)</DialogTitle>
            <DialogDescription>Quando o cliente pedir teste, o sistema gera e envia sozinho — sem você fazer nada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="flex items-center justify-between rounded-lg bg-muted/40 p-3 cursor-pointer">
              <div>
                <span className="text-sm font-medium flex items-center gap-2">⚡ Gerar teste automático</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">Gera o teste e manda os dados no WhatsApp automaticamente.</p>
              </div>
              <Switch checked={!!a.teste_enabled} onCheckedChange={(v) => set({ teste_enabled: v })} />
            </label>
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-2.5 text-[11px] text-muted-foreground space-y-1">
              <p><b className="text-foreground">🤖 Com a IA de atendimento LIGADA:</b> a IA gerencia tudo — pergunta de qual servidor o cliente quer, confere se é o seu Sigma e gera no melhor momento. As palavras-chave são ignoradas.</p>
              <p><b className="text-foreground">💤 Com a IA DESLIGADA:</b> as palavras-chave abaixo disparam o teste direto (sem IA). Ótimo se você só quer o teste automático, sem atendimento por IA.</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Link do teste (chatbot do painel Sigma)</label>
              <Input value={a.teste_link || ''} onChange={(e) => set({ teste_link: e.target.value })}
                placeholder="https://seupainel.top/api/chatbot/xxxx/yyyy" />
              <p className="text-[11px] text-muted-foreground mt-1">No seu painel Sigma: menu do Chatbot → copie o link de gerar teste. Cole aqui.</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Palavras-chave (usadas só quando a IA está DESLIGADA)</label>
              <Input value={(a.teste_keywords || []).join(', ')}
                onChange={(e) => set({ teste_keywords: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                placeholder="teste, testar, quero teste" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Limite de testes por número (0 = ilimitado)</label>
              <Input type="number" min="0" value={a.teste_limite_por_numero ?? 1}
                onChange={(e) => set({ teste_limite_por_numero: parseInt(e.target.value) || 0 })} />
              <p className="text-[11px] text-muted-foreground mt-1">Evita que o mesmo número peça vários testes.</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Mensagem ao atingir o limite</label>
              <Textarea rows={2} value={a.teste_msg_limite || ''} onChange={(e) => set({ teste_msg_limite: e.target.value })}
                placeholder="Ex: Você já solicitou o teste disponível. Para assinar, fale com o suporte. 😊" />
              <p className="text-[11px] text-muted-foreground mt-1">Enviada quando o número já atingiu o limite. Deixe VAZIO para não responder nada.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutorial */}
      <Dialog open={modal === 'tutorial'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> Como configurar sua IA</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <Step n="1" title="Pegue sua chave Gemini (grátis)">É obrigatória. Vá em <a className="text-primary underline" href="https://aistudio.google.com/api-keys" target="_blank" rel="noreferrer">aistudio.google.com/api-keys</a>, crie a chave (começa com <code className="bg-muted px-1 rounded">AIza…</code>) e cole no card <b>Chave Gemini</b>.</Step>
            <Step n="2" title="Preencha os dados do negócio">No card <b>Dados do negócio</b>: o que vende, preços, como paga, horário e regras. A IA usa tudo para atender igual você.</Step>
            <Step n="3" title="Ajuste a personalidade">No card <b>Personalidade</b>: escolha se é vendedor ou suporte, o tom de voz, emojis e o tamanho das respostas.</Step>
            <Step n="4" title="Ligue e teste">Ative no topo e mande uma mensagem de outro número para o seu WhatsApp conectado. A IA responde sozinha! 🚀</Step>
          </div>
          <DialogFooter><Button onClick={() => setModal(null)}>Entendi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center shrink-0 text-sm">{n}</div>
      <div><p className="font-semibold">{title}</p><p className="text-muted-foreground text-[13px] mt-0.5">{children}</p></div>
    </div>
  );
}

function KeywordEditor({ label, value, onChange, placeholder, numeric }) {
  const [input, setInput] = useState('');
  const add = () => {
    let v = input.trim();
    if (numeric) v = v.replace(/\D/g, '');
    if (v && !value.includes(v)) { onChange([...value, v]); setInput(''); }
  };
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <Button type="button" variant="outline" onClick={add}>Adicionar</Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((k) => (
            <Badge key={k} variant="outline" className="gap-1 pr-1">{k}
              <button onClick={() => onChange(value.filter((x) => x !== k))} className="hover:text-red-500"><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
